import { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'wouter'
import { Card } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import {
	AlertCircle,
	Check,
	Clock,
	ChevronLeft,
	ChevronRight,
	Loader2,
	Ticket,
	ExternalLink,
	Phone
} from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { apiFetch } from '@/utils'
import { useUser } from '@/hooks/use-user'
import { useGetActiveMemberships } from '@/hooks/use-get-active-memberships'
import { getSignedTransactionData } from '@/utils/transactions'
import { useGetActiveEntryPass } from '@/hooks/use-get-active-entry-pass'
import { usePrices } from '@/hooks/use-prices'
import { mutate } from 'swr'
import PaymentConfirmationDialog from '@/components/PaymentConfirmationDialog'
import { IS_TESTNET } from '@/constants/walletConnectionConfig'
import AvailableEntryPassCard from '@/components/AvailableEntryPassCard'
import { EntryPassTemplate, GuestType } from '@/constants'
import { Button } from '@/components/ui/button'
import ConfirmModal from '@/components/common/ConfirmationModal'
import useSWR from 'swr'

interface TaxCalculationResult {
	subtotal: number
	fees: Array<{
		type: string
		rate: number
		amount: number
		description: string
	}>
	total: number
}

interface EnhancedPassOption {
	id: number
	name: string
	price: number
	startTime: string
	endTime: string
	dayOfWeek: number
	timeSlot: string
	description: string
	isAvailable: boolean
	guestType: GuestType
	usdPrice: number
	sechPrice: number
	taxCalculation?: TaxCalculationResult
	totalSechPrice?: number
}

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function isTemplateAvailableNow(template: EntryPassTemplate): boolean {
	const now = new Date()
	const currentDay = now.getDay()

	const [startHour, startMinute] = template.startTime.split(':').map(Number)
	const [endHour, endMinute] = template.endTime.split(':').map(Number)

	const currentMinutes = now.getHours() * 60 + now.getMinutes()
	const start = startHour * 60 + startMinute
	const end = endHour * 60 + endMinute

	const isOvernight = end < start

	if (template.dayOfWeek === currentDay) {
		// Template is for today
		if (isOvernight) {
			// Overnight pass (e.g., 18:00 - 02:00): available from start until midnight
			return currentMinutes >= start
		}
		// Same-day pass: available within the time range
		return currentMinutes >= start && currentMinutes <= end
	}

	// Check if this is yesterday's overnight pass still active today
	const previousDay = (currentDay + 6) % 7 // yesterday's day of week
	if (template.dayOfWeek === previousDay && isOvernight) {
		// Yesterday's overnight pass: available from midnight until end time
		return currentMinutes <= end
	}

	return false
}

function getTimeSlotDescription(timeSlot: string): string {
	return timeSlot === 'daytime' ? 'Daytime entry' : 'Evening entry'
}

export default function EntryPassesPage() {
	const [isProcessing, setIsProcessing] = useState(false)
	const [selectedPass, setSelectedPass] = useState<number | null>(null)
	const [selectedGuestType, setSelectedGuestType] = useState<GuestType | null>(null)
	const [validationError, setValidationError] = useState<string | null>(null)
	const [isValidating, setIsValidating] = useState(true)
	const [taxCalculations, setTaxCalculations] = useState<{ [key: string]: TaxCalculationResult }>({})
	const [isCalculatingTaxes, setIsCalculatingTaxes] = useState(false)
	const [, navigate] = useLocation()
	const { toast } = useToast()
	const [currentTime, setCurrentTime] = useState(new Date())

	const { uid, isConnected, walletAddress, loading, connectWallet } = useUser()
	const { convertUsdToToken } = usePrices()
	const {
		activeMemberships,
		isLoading: isMembershipLoading,
		hasActiveMemberships,
		hasAllMembershipsExpired,
		mostRecentActiveMembership
	} = useGetActiveMemberships(walletAddress)
	const {
		activeEntryPass: currentEntryPass,
		isEntryPassLoading: isCurrentEntryPassLoading,
		refetch: refetchActiveEntryPass
	} = useGetActiveEntryPass(walletAddress)

	const [isConfirmingPassPurchase, setIsConfirmingPassPurchase] = useState(false)
	const [showBookingConfirm, setShowBookingConfirm] = useState(false)

	// Auto-set guest type from active membership
	useEffect(() => {
		if (mostRecentActiveMembership?.guestType) {
			setSelectedGuestType(mostRecentActiveMembership.guestType)
		}
	}, [mostRecentActiveMembership])

	// Fetch templates from API
	const { data: templatesResponse, error: templatesError, isLoading: isTemplatesLoading } = useSWR<{ success: boolean; data: EntryPassTemplate[] }>(
		'/v1/entry-passes/templates',
		async () => {
			const response = await apiFetch('/v1/entry-passes/templates', {
				withCredentials: true
			})
			return response.json()
		}
	)

	const templates = templatesResponse?.data || []

	// Filter to active templates for today + previous day's overnight passes
	const availableTemplates = useMemo(() => {
		const today = currentTime.getDay()
		const yesterday = (today + 6) % 7
		return templates.filter(t => {
			if (!t.isActive) return false
			// Include today's templates
			if (t.dayOfWeek === today) return true
			// Include yesterday's overnight passes only if still within their end time
			if (t.dayOfWeek === yesterday) {
				const [startHour, startMinute] = t.startTime.split(':').map(Number)
				const [endHour, endMinute] = t.endTime.split(':').map(Number)
				const start = startHour * 60 + startMinute
				const end = endHour * 60 + endMinute
				const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes()
				return end < start && currentMinutes <= end
			}
			return false
		})
	}, [templates, currentTime])

	// Update time every minute
	useEffect(() => {
		const timer = setInterval(() => {
			setCurrentTime(new Date())
		}, 60000)

		return () => clearInterval(timer)
	}, [])

	// Get price for a template based on guest type
	const getPriceForGuestType = (template: EntryPassTemplate, guestType: GuestType): number => {
		switch (guestType) {
			case 'couples':
				return parseFloat(template.priceCouples) || 0
			case 'single_ladies':
				return parseFloat(template.priceSingleLadies) || 0
			case 'single_males':
				return parseFloat(template.priceSingleMales) || 0
			default:
				return parseFloat(template.priceCouples) || 0
		}
	}

	// Enhanced passes with SECH pricing and tax calculations
	const passesWithSechPricing = useMemo(() => {
		if (!selectedGuestType) return []
		return availableTemplates.map(template => {
			const usdPrice = getPriceForGuestType(template, selectedGuestType)
			const sechPrice = convertUsdToToken(usdPrice, 'sech')
			const taxKey = `${template.id}-${selectedGuestType}`
			const taxCalculation = taxCalculations[taxKey]
			const totalSechPrice = taxCalculation ? convertUsdToToken(taxCalculation.total, 'sech') : sechPrice
			const isAvailable = isTemplateAvailableNow(template)

			return {
				id: template.id,
				name: template.name,
				price: usdPrice,
				startTime: template.startTime,
				endTime: template.endTime,
				dayOfWeek: template.dayOfWeek,
				timeSlot: template.timeSlot,
				description: getTimeSlotDescription(template.timeSlot),
				isAvailable,
				guestType: selectedGuestType,
				usdPrice,
				sechPrice,
				taxCalculation,
				totalSechPrice
			} as EnhancedPassOption
		})
	}, [availableTemplates, selectedGuestType, convertUsdToToken, taxCalculations])

	// Calculate taxes for all passes when guest type changes
	useEffect(() => {
		if (availableTemplates.length === 0 || !selectedGuestType) return

		const calculateTaxes = async () => {
			setIsCalculatingTaxes(true)
			const calculations: { [key: string]: TaxCalculationResult } = {}

			try {
				const taxPromises = availableTemplates.map(async (template) => {
					const price = getPriceForGuestType(template, selectedGuestType)
					const taxKey = `${template.id}-${selectedGuestType}`

					try {
						const response = await apiFetch('/v1/taxes/calculate', {
							method: 'POST',
							headers: {
								'Content-Type': 'application/json',
							},
							body: JSON.stringify({
								amount: price,
								transactionType: 'entry_pass'
							})
						})

						if (!response.ok) {
							throw new Error(`HTTP error! status: ${response.status}`)
						}

						const result = await response.json()
						if (result.success) {
							return { key: taxKey, data: result.data }
						} else {
							throw new Error(result.message || 'Tax calculation failed')
						}
					} catch (error) {
						console.error(`Failed to calculate taxes for template ${template.id}:`, error)
						return {
							key: taxKey,
							data: {
								subtotal: price,
								fees: [],
								total: price
							}
						}
					}
				})

				const results = await Promise.all(taxPromises)

				results.forEach(result => {
					if (result) {
						calculations[result.key] = result.data
					}
				})

			} catch (error) {
				console.error('Error calculating taxes:', error)
				toast({
					title: 'Tax Calculation Error',
					description: 'Unable to calculate taxes. Showing base prices.',
					variant: 'destructive'
				})

				availableTemplates.forEach(template => {
					const price = getPriceForGuestType(template, selectedGuestType)
					const taxKey = `${template.id}-${selectedGuestType}`
					calculations[taxKey] = {
						subtotal: price,
						fees: [],
						total: price
					}
				})
			} finally {
				setTaxCalculations(prev => ({ ...prev, ...calculations }))
				setIsCalculatingTaxes(false)
			}
		}

		calculateTaxes()
	}, [availableTemplates, selectedGuestType, toast])

	const selectedPassPrice = useMemo(() => {
		const pass = passesWithSechPricing.find((p) => p.id === selectedPass)
		return pass?.totalSechPrice || 0
	}, [passesWithSechPricing, selectedPass])

	// Enhanced validation for direct access
	useEffect(() => {
		const validateAccess = async () => {
			setIsValidating(true)
			setValidationError(null)
			if (isCurrentEntryPassLoading || isMembershipLoading || loading) return

			try {
				if (!hasActiveMemberships) {
					setValidationError(
						'Please select a membership plan before accessing passes.'
					)
					setTimeout(() => navigate('/membership'), 3000)
					return
				}

				if (hasAllMembershipsExpired) {
					localStorage.removeItem('selectedMembership')
					setValidationError(
						'Your membership selection has expired. Please select a plan again.'
					)
					setTimeout(() => navigate('/membership'), 3000)
					return
				}

				if (walletAddress) {
					const response = await apiFetch(`/v1/waivers/my`, {
						withCredentials: true
					})
					if (!response.ok) {
						throw new Error('Failed to verify waiver status')
					}

					const waiverStatus = await response.json()

					if (!waiverStatus.completed) {
						setValidationError(
							'Please complete both required waivers before accessing passes.'
						)
						setTimeout(() => navigate('/waivers'), 3000)
						return
					}
				}
			} catch (error) {
				console.error('Validation error:', error)
				setValidationError(
					'An error occurred while validating your access. Please try again.'
				)
				setTimeout(() => navigate('/membership'), 3000)
			} finally {
				setIsValidating(false)
			}
		}

		validateAccess()
	}, [
		navigate,
		loading,
		walletAddress,
		hasAllMembershipsExpired,
		isMembershipLoading,
		activeMemberships,
		isCurrentEntryPassLoading
	])

	const handlePurchase = async (passId: number) => {
		setIsConfirmingPassPurchase(false)

		const pass = passesWithSechPricing.find((p) => p.id === passId)
		if (!pass || !pass.isAvailable) {
			toast({
				title: 'Pass Unavailable',
				description: 'This pass is not available at the current time.',
				variant: 'destructive'
			})
			return
		}

		if (!pass.taxCalculation) {
			toast({
				title: 'Tax Calculation Error',
				description: 'Tax calculation not available. Please try again.',
				variant: 'destructive'
			})
			return
		}

		setIsProcessing(true)
		try {
			toast({
				title: 'Processing Payment',
				description: `Processing your ${pass.name} purchase...`
			})

			if (!walletAddress) {
				await connectWallet()
				if (!walletAddress) {
					throw new Error('Please connect your wallet to continue')
				}
			}

			let signature
			let nonce
			let forwardRequest

			if (isConnected) {
				const {
					signedTx,
					txNonce,
					transferRequest: signatureData
				} = await getSignedTransactionData({
					userAddress: walletAddress,
					amount: pass.totalSechPrice?.toString() || pass.sechPrice.toString()
				})
				signature = signedTx
				nonce = txNonce
				forwardRequest = signatureData
			}

			const response = await apiFetch('/v1/entry-passes', {
				method: 'POST',
				body: JSON.stringify({
					userUid: uid,
					customerAddress: walletAddress,
					templateId: pass.id,
					guestType: selectedGuestType,
					signature,
					forwardRequest
				})
			})

			if (!response.ok) {
				const errorData = await response.json()
				throw new Error(errorData.message || 'Failed to record entry pass')
			}

			localStorage.removeItem('selectedMembership')
			setSelectedPass(null)

			await mutate(`/v1/transactions/${walletAddress}`)
			refetchActiveEntryPass()

			toast({
				title: 'Success!',
				description: `Your ${pass.name} has been activated.`
			})

			navigate('/entry-pass')

		} catch (error) {
			toast({
				title: 'Purchase Failed',
				description:
					error instanceof Error ? error.message : 'Failed to purchase pass',
				variant: 'destructive'
			})
		} finally {
			setIsProcessing(false)
		}
	}

	const formatPrice = (amount: number) => {
		return Number(amount).toFixed(IS_TESTNET ? 6 : 2)
	}

	const TaxBreakdownCard = ({ pass }: { pass: EnhancedPassOption }) => {
		if (!pass.taxCalculation) {
			return (
				<Card className="mt-4 p-4 bg-[#3C3C3C] border-0">
					<div className="flex items-center justify-center py-4">
						<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
						<span className="ml-2 text-sm text-[#BABABB]">Calculating taxes...</span>
					</div>
				</Card>
			)
		}

		const { taxCalculation } = pass

		return (
			<Card className="mt-4 p-4 bg-[#3C3C3C] border-0">
				<h4 className="font-semibold mb-2 flex items-center text-[#ffffff]">
					<Check className="h-4 w-4 mr-2 text-green-500" />
					Price Breakdown
				</h4>
				<div className="space-y-2 text-sm">
					<div className="flex justify-between">
						<span className="text-[#BABABB]">Subtotal:</span>
						<div className="text-right">
							<div className="text-[#ffffff]">${formatPrice(taxCalculation.subtotal)} USD</div>
							<div className="text-xs text-[#BABABB]">
								{formatPrice(convertUsdToToken(taxCalculation.subtotal, 'sech'))} SECH
							</div>
						</div>
					</div>
					{taxCalculation.fees.map((fee, index) => (
						<div key={index} className="flex justify-between text-[#BABABB]">
							<span>{fee.description} ({(fee.rate * 100).toFixed(1)}%):</span>
							<div className="text-right">
								<div>${formatPrice(fee.amount)} USD</div>
								<div className="text-xs">
									{formatPrice(convertUsdToToken(fee.amount, 'sech'))} SECH
								</div>
							</div>
						</div>
					))}
					<hr className="my-2 border-[#4C4C4C]" />
					<div className="flex justify-between font-semibold">
						<span className="text-[#ffffff]">Total:</span>
						<div className="text-right">
							<div className="text-[#ffffff]">${formatPrice(taxCalculation.total)} USD</div>
							<div className="text-sm text-primary font-semibold">
								{formatPrice(pass.totalSechPrice || 0)} SECH
							</div>
						</div>
					</div>
				</div>
			</Card>
		)
	}

	if (isValidating) {
		return (
			<div className="min-h-screen bg-[#18181B] flex items-center justify-center">
				<Card className="p-6 text-center bg-[#3C3C3C] border-0">
					<Clock className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
					<h2 className="text-lg font-semibold mb-2 text-white">Validating Access</h2>
						<p className="text-sm text-[#BABABB]">
						Please wait while we verify your membership and waivers...
					</p>
				</Card>
			</div>
		)
	}

	if (validationError) {
		return (
			<div className="min-h-auto bg-[#18181B]">
				<div className="px-5 py-4 flex items-center justify-center min-h-[60vh]">
					<Alert variant="destructive" className="max-w-lg">
						<AlertCircle className="h-4 w-4" />
						<AlertDescription className="flex flex-col gap-2">
							<p>{validationError}</p>
							<p className="text-sm">Redirecting you shortly...</p>
						</AlertDescription>
					</Alert>
				</div>
			</div>
		)
	}

	return (
		<div className="min-h-auto bg-[#18181B]">
			<div className="max-w-5xl mx-auto px-5 pb-8">
				<div
					role="banner"
					className="z-50 sticky top-0 bg-[#18181B] pb-[12px] backdrop-blur-[8px] mb-[12px] h-auto flex items-center justify-between text-white py-3"
				>
					<div className="flex items-center gap-2">
						<button
							type="button"
							aria-label="Go back"
							onClick={() => navigate('/')}
							className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
						>
							<ChevronLeft className='h-[22px] w-[22px] text-[#ffffff]' />
						</button>

						<h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">
							Entry Passes
						</h1>
					</div>
					<div className="flex items-center text-sm text-[#BABABB]">
						<Clock className="h-4 w-4 mr-1.5" />
						<span>{currentTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</span>
					</div>
				</div>

				{currentEntryPass && (
					<button
						onClick={() => navigate('/entry-pass')}
						className="w-full mb-4 flex items-center justify-between bg-green-500/15 border border-green-500/30 rounded-[12px] px-4 py-3"
					>
						<div className="flex items-center gap-3">
							<div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center">
								<Ticket className="h-5 w-5 text-green-500" />
							</div>
							<div className="text-left">
								<p className="text-white font-semibold text-sm">Active Entry Pass</p>
								<p className="text-green-400 text-xs">Tap to view your ticket</p>
							</div>
						</div>
						<ChevronRight className="h-5 w-5 text-green-400" />
					</button>
				)}

				<div className="text-start mb-6">
					<h1 className="text-[16px] leading-[18px] font-semibold font-figtree text-[#ffffff] mb-2">
						{DAYS_OF_WEEK[currentTime.getDay()]} Entry Passes
					</h1>
					<p className="text-[13px] leading-[15px] font-normal text-[#BABABB]">
						Choose a pass. All prices include applicable taxes.
					</p>
				</div>

				{isTemplatesLoading ? (
					<div className="flex items-center justify-center py-12">
						<Loader2 className="h-8 w-8 animate-spin text-primary" />
						<span className="ml-2 text-[#BABABB]">Loading entry passes...</span>
					</div>
				) : templatesError ? (
					<Alert variant="destructive">
						<AlertCircle className="h-4 w-4" />
						<AlertDescription>
							Failed to load entry passes. Please try again later.
						</AlertDescription>
					</Alert>
				) : availableTemplates.length === 0 ? (
					<Card className="p-6 bg-[#3C3C3C] border-0 text-center">
						<p className="text-[#BABABB]">
							No entry passes available for {DAYS_OF_WEEK[currentTime.getDay()]}. Please check back later.
						</p>
					</Card>
				) : (
					<>
						<div className="flex flex-wrap justify-center gap-4">
							{passesWithSechPricing.map((pass) => (
								<div key={pass.id} className="flex flex-col w-full md:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.75rem)]">
									<AvailableEntryPassCard
										pass={pass}
										isSelected={selectedPass === pass.id}
										isCurrent={!!currentEntryPass && currentEntryPass.templateId === pass.id}
										canBuyToday={!isProcessing}
										isLoading={isCalculatingTaxes}
										isProcessing={isProcessing && selectedPass === pass.id}
										onSelect={(clickedPass) => {
											if (isCalculatingTaxes || !clickedPass.taxCalculation || isProcessing || clickedPass.price === 0 || (currentEntryPass && currentEntryPass.templateId === clickedPass.id)) {
												return
											}
											if (selectedPass === clickedPass.id) {
												setIsConfirmingPassPurchase(true)
											} else {
												setSelectedPass(clickedPass.id)
											}
										}}
									/>
									{selectedPass === pass.id && <TaxBreakdownCard pass={pass} />}
								</div>
							))}
						</div>
						{isCalculatingTaxes && (
							<p className="text-primary mt-4 flex items-center justify-center gap-2 text-sm">
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
								Calculating taxes...
							</p>
						)}
					</>
				)}
				{/* Book a Room CTA */}
				<div className="mt-8 rounded-[16px] bg-[#2F2F32] border border-white/10 overflow-hidden">
					<div className="px-6 py-5 text-center">
						<h3 className="text-lg font-semibold text-white mb-1">Need to Book a Room?</h3>
						<p className="text-sm text-[#BABABB] mb-4">Reserve your Room at Secrets Hideaways</p>
						<Button
							onClick={() => setShowBookingConfirm(true)}
							className="w-full h-[42px] bg-[#EF4444] hover:bg-[#DC2626] rounded-[21px] shadow-[0px_4px_36px_0px_#DC262699] text-white font-semibold flex items-center justify-center gap-2"
						>
							<ExternalLink className="h-4 w-4" />
							Book Now
						</Button>
						<div className="mt-4 border-t border-white/10 pt-4">
							<p className="text-sm text-[#BABABB] mb-2">Or Call Toll Free</p>
							<a
								href="tel:8445636969"
								className="inline-flex items-center gap-2 text-primary font-semibold text-lg hover:underline"
							>
								<Phone className="h-4 w-4" />
								844-563-6969
							</a>
						</div>
					</div>
				</div>
			</div>
			<PaymentConfirmationDialog
				open={isConfirmingPassPurchase}
				amount={selectedPassPrice}
				onConfirm={() => handlePurchase(selectedPass!)}
				onCancel={() => setIsConfirmingPassPurchase(false)}
			/>
			<ConfirmModal
				open={showBookingConfirm}
				onClose={() => setShowBookingConfirm(false)}
				onConfirm={() => {
					setShowBookingConfirm(false)
					window.open('https://www.secretsfl.com/book-now-iframe', '_blank')
				}}
				title="You're leaving the app"
				description="You will be redirected to the Secrets Hideaways website to complete your room booking. The booking site's terms and policies will apply."
				confirmText="Continue to Booking"
				cancelText="Stay in SECH"
			/>
		</div>
	)
}
